import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { oneKlicks } from "../../../SDK/oneclick";
import { toast } from "react-toastify";
import { Form, InputGroup, Badge } from "react-bootstrap";
import { Button, Select } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";

const parseMetaPayload = (metaValue) => {
    if (!metaValue) {
        return {};
    }
    if (typeof metaValue === "object") {
        return metaValue || {};
    }
    try {
        return JSON.parse(metaValue);
    } catch (error) {
        console.warn("Unable to parse meta payload", error);
        return {};
    }
};

const toNumericOrNull = (value) => {
    if (value === undefined || value === null || value === "") {
        return null;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const recordMatchesInsuranceUser = (record, userData) => {
    if (!record || !userData?.id) {
        return false;
    }

    const userId = toNumericOrNull(userData.id);
    const companyId = toNumericOrNull(userData.company_id || userData.companyId || userData.company);
    const agentId = toNumericOrNull(userData.agent_id || userData.agentId);

    const recordCompanyId = toNumericOrNull(record.company_id || record.companyId);
    const recordAgentId = toNumericOrNull(record.agent_id || record.agentId);

    if (companyId && recordCompanyId && companyId === recordCompanyId) {
        return true;
    }

    if (agentId && recordAgentId && agentId === recordAgentId) {
        return true;
    }

    const directUserCandidates = [
        record.user_id,
        record.userId,
        record.created_by,
        record.createdBy,
        record.owner_id,
        record.ownerId,
        record.agent_user_id,
    ]
        .map(toNumericOrNull)
        .filter((value) => value !== null);

    if (directUserCandidates.includes(userId)) {
        return true;
    }

    const meta = parseMetaPayload(record.meta);
    const metaCandidates = [
        meta?.created_by,
        meta?.user_id,
        meta?.owner_id,
        meta?.agent_id,
    ]
        .map(toNumericOrNull)
        .filter((value) => value !== null);

    return metaCandidates.includes(userId);
};

const LEAD_STATUSES = [
    { key: "new", label: "New", description: "Fresh inquiries awaiting contact" },
    { key: "contacted", label: "Contacted", description: "Initial conversation in progress" },
    { key: "follow_up", label: "Follow-up", description: "Requires scheduled follow-up" },
    { key: "proposal", label: "Proposal", description: "Proposal/quote shared" },
    { key: "negotiation", label: "Negotiation", description: "Discussing coverage & pricing" },
    { key: "won", label: "Won", description: "Converted to policy" },
    { key: "lost", label: "Lost", description: "Closed without conversion" },
];

const LEAD_PRIORITIES = [
    { key: "high", label: "High" },
    { key: "medium", label: "Medium" },
    { key: "low", label: "Low" },
];

const sourceOptions = [
    "Website",
    "Referral",
    "Campaign",
    "Walk-in",
    "Agent Outreach",
    "Social Media",
    "Corporate",
];

const viewModes = [
    { key: "board", label: "Pipeline" },
    { key: "table", label: "Table" },
];

const Leads = () => {
    const { userData } = useAuth();
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [updatingLeadId, setUpdatingLeadId] = useState(null);
    const [leadForm, setLeadForm] = useState({
        lead_name: "",
        lead_email: "",
        lead_phone: "",
        status: "new",
        priority: "medium",
        source: "Website",
        notes: "",
        follow_up_date: "",
    });
    const [viewMode, setViewMode] = useState("table");
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [priorityFilter, setPriorityFilter] = useState("all");
    const [sourceFilter, setSourceFilter] = useState("all");
    const [showAddForm, setShowAddForm] = useState(false);

    const companyId = useMemo(
        () => toNumericOrNull(userData?.company_id || userData?.companyId || userData?.company),
        [userData]
    );
    const agentId = useMemo(
        () => toNumericOrNull(userData?.agent_id || userData?.agentId),
        [userData]
    );

    useEffect(() => {
        fetchLeads();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData?.id, companyId, agentId]);

    const fetchLeads = async () => {
        if (!userData?.id) {
            setLeads([]);
            return;
        }

        try {
            setLoading(true);
            const conditions = {};
            if (companyId) {
                conditions.company_id = companyId;
            }
            if (!conditions.company_id && agentId) {
                conditions.agent_id = agentId;
            }
            if (!conditions.company_id && !conditions.agent_id) {
                conditions.user_id = userData.id;
            }

            const response = await oneKlicks.read(
                "",
                "insurance_leads",
                conditions,
                "updated_at DESC",
                "",
                "",
                "0,500"
            );

            if (response?.success) {
                const rows = Array.isArray(response?.data)
                    ? response.data
                    : Array.isArray(response?.data?.items)
                        ? response.data.items
                        : [];
                const filteredLeads = rows
                    .filter((lead) => recordMatchesInsuranceUser(lead, userData))
                    .map((lead) => {
                        const meta = parseMetaPayload(lead.meta);
                        return {
                            ...lead,
                            lead_name: lead.lead_name || lead.name || meta?.lead_name || `Lead #${lead.id}`,
                            lead_email: lead.lead_email || lead.email || meta?.email || "",
                            lead_phone: lead.lead_phone || lead.phone || meta?.phone || "",
                            status: (lead.status || lead.pipeline_stage || "new").toLowerCase(),
                            priority: (lead.priority || lead.lead_priority || meta?.priority || "medium").toLowerCase(),
                            source: (lead.source || meta?.source || "Website"),
                            follow_up_date: lead.follow_up_date || meta?.follow_up_date || "",
                            notes: lead.notes || meta?.notes || "",
                            company_name: lead.company_name || meta?.company_name || "",
                            assigned_to: lead.assigned_to || meta?.assigned_to || "",
                            budget: lead.budget || meta?.budget || null,
                            product_interest: lead.product_interest || meta?.product_interest || "",
                            last_contacted_at: lead.last_contacted_at || meta?.last_contacted_at || "",
                        };
                    });
                setLeads(filteredLeads);
            } else {
                setLeads([]);
            }
        } catch (error) {
            console.error("Error fetching leads:", error);
            toast.error("Unable to load leads");
            setLeads([]);
        } finally {
            setLoading(false);
        }
    };

    const resetLeadForm = () => {
        setLeadForm({
            lead_name: "",
            lead_email: "",
            lead_phone: "",
            status: "new",
            priority: "medium",
            source: "Website",
            notes: "",
            follow_up_date: "",
        });
    };

    const handleLeadFormChange = (event) => {
        const { name, value } = event.target;
        setLeadForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleCreateLead = async (event) => {
        event.preventDefault();
        if (!leadForm.lead_name?.trim()) {
            toast.error("Lead name is required");
            return;
        }

        if (!userData?.id) {
            toast.error("User information is missing. Please login again.");
            return;
        }

        try {
            setCreating(true);
            const timestamp = new Date().toISOString().slice(0, 19).replace("T", " ");
            const cleanName = leadForm.lead_name.trim();
            const cleanEmail = leadForm.lead_email?.trim() || null;
            const cleanPhone = leadForm.lead_phone?.trim() || null;
            const cleanNotes = leadForm.notes?.trim() || null;
            const payload = {
                lead_name: cleanName,
                name: cleanName,
                lead_email: cleanEmail,
                email: cleanEmail,
                lead_phone: cleanPhone,
                phone: cleanPhone,
                status: leadForm.status,
                lead_status: leadForm.status,
                pipeline_stage: leadForm.status,
                priority: leadForm.priority,
                lead_priority: leadForm.priority,
                source: leadForm.source,
                lead_source: leadForm.source,
                notes: cleanNotes,
                follow_up_date: leadForm.follow_up_date || null,
                user_id: userData.id,
                company_id: companyId,
                agent_id: agentId,
                created_at: timestamp,
                updated_at: timestamp,
                meta: JSON.stringify({
                    created_by: userData.id,
                    priority: leadForm.priority,
                    source: leadForm.source,
                    follow_up_date: leadForm.follow_up_date,
                    notes: cleanNotes,
                }),
            };

            console.log("Creating insurance lead payload:", payload);

            const response = await oneKlicks.create("", "insurance_leads", payload);
            console.log("Create lead response:", response);

            if (response?.success) {
                toast.success("Lead created successfully");
                resetLeadForm();
                setShowAddForm(false);
                fetchLeads();
            } else {
                toast.error(response?.message || response?.error || "Failed to create lead");
            }
        } catch (error) {
            console.error("Error creating lead:", error);
            toast.error("Failed to create lead");
        } finally {
            setCreating(false);
        }
    };

    const handleStatusChange = async (leadId, status) => {
        if (!leadId) return;
        try {
            setUpdatingLeadId(leadId);
            const payload = {
                status,
                updated_at: new Date().toISOString().slice(0, 19).replace("T", " "),
            };
            const response = await oneKlicks.update("", "insurance_leads", payload, { id: leadId });
            if (response?.success) {
                fetchLeads();
            } else {
                toast.error(response?.message || response?.error || "Failed to update lead");
            }
        } catch (error) {
            console.error("Error updating lead status:", error);
            toast.error("Failed to update lead");
        } finally {
            setUpdatingLeadId(null);
        }
    };

    const filteredLeads = useMemo(() => {
        return leads.filter((lead) => {
            const matchesSearch = [
                lead.lead_name,
                lead.lead_email,
                lead.lead_phone,
                lead.source,
                lead.product_interest,
                lead.company_name,
                lead.assigned_to,
            ]
                .filter(Boolean)
                .some((value) => value.toString().toLowerCase().includes(searchTerm.toLowerCase()));

            const matchesStatus = statusFilter === "all" || lead.status === statusFilter;
            const matchesPriority = priorityFilter === "all" || lead.priority === priorityFilter;
            const matchesSource = sourceFilter === "all" || lead.source === sourceFilter;

            return matchesSearch && matchesStatus && matchesPriority && matchesSource;
        });
    }, [leads, searchTerm, statusFilter, priorityFilter, sourceFilter]);

    const leadsByStatus = useMemo(() => {
        return LEAD_STATUSES.reduce((acc, status) => {
            acc[status.key] = filteredLeads.filter((lead) => (lead.status || "new") === status.key);
            return acc;
        }, {});
    }, [filteredLeads]);

    const totalLeads = filteredLeads.length;
    const hotLeads = filteredLeads.filter((lead) => ["high", "urgent", "hot"].includes(lead.priority)).length;
    const upcomingFollowUps = filteredLeads.filter((lead) => {
        if (!lead.follow_up_date) return false;
        const followDate = new Date(lead.follow_up_date);
        if (Number.isNaN(followDate.getTime())) return false;
        const now = new Date();
        const diff = followDate.getTime() - now.getTime();
        return diff >= 0 && diff <= 1000 * 60 * 60 * 24 * 7; // within 7 days
    }).length;
    const wonLeads = filteredLeads.filter((lead) => lead.status === "won").length;
    const conversionRate = totalLeads ? Math.round((wonLeads / totalLeads) * 100) : 0;

    return (
        <div className="subbody" style={{ width: "100%", padding: "2% 4%" }}>
            <div className="dc-haslayout dc-dbsectionspace">
                <div className="container-fluid py-3">
                    {/* Leads Management Header */}
                    <div className="card mb-5" style={{ borderRadius: "5px" }}>
                        <div className="card-body" style={{ padding: '24px 30px', borderRadius: "5px" }}>
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <div className="d-flex align-items-center gap-2">
                                    <h5 className="mb-0 font-weight-bold">Leads Management</h5>
                                    <Badge bg="info" style={{ borderRadius: "5px" }}>Leads</Badge>
                                </div>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <Button 
                                        className="btn-primary" 
                                        onClick={() => {
                                            setShowAddForm(!showAddForm);
                                            if (!showAddForm) {
                                                setTimeout(() => {
                                                    document.getElementById('add-lead-form')?.scrollIntoView({ behavior: 'smooth' });
                                                }, 100);
                                            }
                                        }}
                                        style={{ height: '40px', borderRadius: "5px" }}
                                    >
                                        <PlusOutlined /> {showAddForm ? 'Hide Form' : 'Add New Lead'}
                                    </Button>
                                </div>
                            </div>
                            <div className="d-flex flex-wrap align-items-center gap-3" style={{ marginBottom: '20px' }}>
                                <div style={{ flex: '1', minWidth: '250px', maxWidth: '400px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                                        Search
                                    </label>
                                    <InputGroup>
                                        <Form.Control
                                            placeholder="Search leads..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            style={{ height: '40px', borderRadius: "5px" }}
                                        />
                                    </InputGroup>
                                </div>
                                <div style={{ minWidth: '180px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                                        Status
                                    </label>
                                    <Select
                                        value={statusFilter}
                                        onChange={(value) => setStatusFilter(value)}
                                        style={{ width: '100%' }}
                                        size="large"
                                        options={[
                                            { value: 'all', label: 'All Status' },
                                            ...LEAD_STATUSES.map(status => ({ value: status.key, label: status.label }))
                                        ]}
                                    />
                                </div>
                                <div style={{ minWidth: '180px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                                        Priority
                                    </label>
                                    <Select
                                        value={priorityFilter}
                                        onChange={(value) => setPriorityFilter(value)}
                                        style={{ width: '100%' }}
                                        size="large"
                                        options={[
                                            { value: 'all', label: 'All Priority' },
                                            ...LEAD_PRIORITIES.map(priority => ({ value: priority.key, label: priority.label }))
                                        ]}
                                    />
                                </div>
                                <div style={{ minWidth: '180px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                                        Source
                                    </label>
                                    <Select
                                        value={sourceFilter}
                                        onChange={(value) => setSourceFilter(value)}
                                        style={{ width: '100%' }}
                                        size="large"
                                        options={[
                                            { value: 'all', label: 'All Sources' },
                                            ...sourceOptions.map(source => ({ value: source, label: source }))
                                        ]}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Leads Table */}
                    <div className="card" style={{ borderRadius: "5px" }}>
                        <div className="card-body" style={{ padding: '24px 30px', borderRadius: "5px" }}>
                            {loading ? (
                                <div className="text-center p-5">
                                    <div className="spinner-border" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                </div>
                            ) : filteredLeads.length > 0 ? (
                                <div className="table-responsive">
                                    <table hover size="sm" className="mb-0 align-middle">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Lead Name</th>
                                                <th>Contact</th>
                                                <th>Source</th>
                                                <th>Priority</th>
                                                <th>Status</th>
                                                <th>Follow-up</th>
                                                <th style={{ width: '150px' }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredLeads.map((lead) => (
                                                <tr key={lead.id}>
                                                    <td data-label="ID">{lead.id}</td>
                                                    <td data-label="Lead Name">
                                                        <div style={{ fontWeight: '500', color: '#333' }}>
                                                            {lead.lead_name}
                                                        </div>
                                                    </td>
                                                    <td data-label="Contact">
                                                        <div>
                                                            {lead.lead_email && (
                                                                <div style={{ fontSize: '13px', color: '#333', marginBottom: '4px' }}>
                                                                    <i className="ti-email" style={{ marginRight: '4px', color: '#666' }}></i>
                                                                    {lead.lead_email}
                                                                </div>
                                                            )}
                                                            {lead.lead_phone && (
                                                                <div style={{ fontSize: '13px', color: '#333' }}>
                                                                    <i className="ti-mobile" style={{ marginRight: '4px', color: '#666' }}></i>
                                                                    {lead.lead_phone}
                                                                </div>
                                                            )}
                                                            {!lead.lead_email && !lead.lead_phone && "—"}
                                                        </div>
                                                    </td>
                                                    <td data-label="Source">
                                                        <span className="badge badge-info" style={{ borderRadius: "5px" }}>
                                                            {lead.source}
                                                        </span>
                                                    </td>
                                                    <td data-label="Priority">
                                                        <span className={`badge ${lead.priority === "high" ? "badge-danger" : lead.priority === "medium" ? "badge-warning" : "badge-secondary"}`} style={{ borderRadius: "5px" }}>
                                                            {lead.priority.charAt(0).toUpperCase() + lead.priority.slice(1)}
                                                        </span>
                                                    </td>
                                                    <td data-label="Status">
                                                        <span className="badge badge-primary" style={{ textTransform: "capitalize", borderRadius: "5px" }}>
                                                            {lead.status.replace(/_/g, " ")}
                                                        </span>
                                                    </td>
                                                    <td data-label="Follow-up" style={{ fontSize: '13px', color: '#666' }}>
                                                        {lead.follow_up_date ? new Date(lead.follow_up_date).toLocaleDateString() : "—"}
                                                    </td>
                                                    <td data-label="Action">
                                                        <div style={{ display: 'flex', flexDirection: 'row', gap: '6px', alignItems: 'center', flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
                                                            <Button 
                                                                href="#" 
                                                                className="dc-update-servicesss fit-content btn-info" 
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    handleStatusChange(lead.id, lead.status === "new" ? "contacted" : lead.status);
                                                                }}
                                                                style={{ borderRadius: "5px" }}
                                                            >
                                                                <EditOutlined style={{ fontSize: '11px', marginRight: '4px' }} />
                                                                Edit
                                                            </Button>
                                                            <Button 
                                                                href="#" 
                                                                className="dc-update-servicesss fit-content btn-danger" 
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    // Handle delete
                                                                }}
                                                                style={{ borderRadius: "5px" }}
                                                            >
                                                                <DeleteOutlined style={{ fontSize: '11px', marginRight: '4px' }} />
                                                                Delete
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="dc-emptydata-holder">
                                    <div className="dc-emptydata">
                                        <div className="dc-emptydetails dc-empty-booking dc-emptyholder-sm">
                                            <span></span>
                                            <em>No leads match your current filters.</em>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Add New Lead Form */}
                    {showAddForm && (
                        <div className="card mt-5" id="add-lead-form" style={{ borderRadius: "5px" }}>
                            <div className="card-body" style={{ padding: "24px 30px", borderRadius: "5px" }}>
                                <div className="d-flex align-items-center justify-content-between mb-4">
                                    <h5 className="mb-0 font-weight-bold">Add New Lead</h5>
                                    <Button 
                                        onClick={() => setShowAddForm(false)}
                                        style={{ borderRadius: "5px" }}
                                    >
                                        Close
                                    </Button>
                                </div>
                            <form onSubmit={handleCreateLead}>
                                <div className="row" style={{ rowGap: "16px" }}>
                                    <div className="col-12 col-md-4">
                                        <label className="form-label">Lead Name *</label>
                                        <Form.Control
                                            type="text"
                                            name="lead_name"
                                            value={leadForm.lead_name}
                                            onChange={handleLeadFormChange}
                                            placeholder="e.g. Ramesh Kumar"
                                            required
                                            style={{ borderRadius: "5px" }}
                                        />
                                    </div>
                                    <div className="col-12 col-md-4">
                                        <label className="form-label">Email</label>
                                        <Form.Control
                                            type="email"
                                            name="lead_email"
                                            value={leadForm.lead_email}
                                            onChange={handleLeadFormChange}
                                            placeholder="lead@email.com"
                                            style={{ borderRadius: "5px" }}
                                        />
                                    </div>
                                    <div className="col-12 col-md-4">
                                        <label className="form-label">Phone</label>
                                        <Form.Control
                                            type="text"
                                            name="lead_phone"
                                            value={leadForm.lead_phone}
                                            onChange={handleLeadFormChange}
                                            placeholder="Contact number"
                                            style={{ borderRadius: "5px" }}
                                        />
                                    </div>
                                    <div className="col-12 col-md-3">
                                        <label className="form-label">Source</label>
                                        <Select
                                            value={leadForm.source}
                                            onChange={(value) => setLeadForm(prev => ({ ...prev, source: value }))}
                                            style={{ width: '100%' }}
                                            size="large"
                                            options={sourceOptions.map(option => ({ value: option, label: option }))}
                                        />
                                    </div>
                                    <div className="col-12 col-md-3">
                                        <label className="form-label">Status</label>
                                        <Select
                                            value={leadForm.status}
                                            onChange={(value) => setLeadForm(prev => ({ ...prev, status: value }))}
                                            style={{ width: '100%' }}
                                            size="large"
                                            options={LEAD_STATUSES.map(status => ({ value: status.key, label: status.label }))}
                                        />
                                    </div>
                                    <div className="col-12 col-md-3">
                                        <label className="form-label">Priority</label>
                                        <Select
                                            value={leadForm.priority}
                                            onChange={(value) => setLeadForm(prev => ({ ...prev, priority: value }))}
                                            style={{ width: '100%' }}
                                            size="large"
                                            options={LEAD_PRIORITIES.map(priority => ({ value: priority.key, label: priority.label }))}
                                        />
                                    </div>
                                    <div className="col-12 col-md-3">
                                        <label className="form-label">Follow-up Date</label>
                                        <Form.Control
                                            type="date"
                                            name="follow_up_date"
                                            value={leadForm.follow_up_date}
                                            onChange={handleLeadFormChange}
                                            style={{ borderRadius: "5px" }}
                                        />
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label">Notes</label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            name="notes"
                                            value={leadForm.notes}
                                            onChange={handleLeadFormChange}
                                            placeholder="Conversation context, requirements, next steps"
                                            style={{ borderRadius: "5px" }}
                                        />
                                    </div>
                                </div>
                                <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "20px" }}>
                                    <Button
                                        onClick={resetLeadForm}
                                        disabled={creating}
                                        style={{ borderRadius: "5px" }}
                                    >
                                        Clear
                                    </Button>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={creating}
                                        style={{ borderRadius: "5px" }}
                                    >
                                        {creating ? "Saving..." : "Add Lead"}
                                    </Button>
                                </div>
                            </form>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Leads;
